import { useAuth0 } from '@auth0/auth0-react';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import ModalComponent from 'components/Common/Modal';
import { useGetSummaryForContent } from 'components/Dashboard/components/ContentSummary';
import CreditsButton from 'components/Dashboard/components/CreditsButton';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import useWindowHeight from 'components/Dashboard/utilities/useWindowHeight';
import useGetReportList from 'components/Reports/useGetReportList';
import DropdownComponent from 'components/Settings/WebsiteFeeds/DropdownComponent';
import { store } from 'components/Store';
import { useContext, useEffect, useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';

const AddUpdatesToReport = (props) => {
  const globalState = useContext(store);
  const { dispatch } = globalState;
  const navigate = useNavigate();
  const {
    isOpen,
    setIsOpen,
    addUpdatesToReportFunction = ({ selectedUpdates, headlineInclude, reportOption, selectedReport }) => {
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'updatesToCreateReport',
        value: {
          selectedUpdates: selectedUpdates,
          headlineOption: headlineInclude,
        },
      });
      navigate({
        pathname: reportOption === 'NewReport' ? '/reports/create' : `/reports/${selectedReport?.id}`,
      });
    },
    selectedUpdates,
    fromMainList,
    setSingleUpdate,
  } = props ?? {};
  const [resetInitialState, setResetInitialState] = useState(false);

  const handleSetIsOpen = (value) => {
    setIsOpen(value);
    if (!value) {
      if (setSingleUpdate) setSingleUpdate(null);
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'createReportFromUpdatePopup',
        value: null,
      });
    }
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      setIsOpen={handleSetIsOpen}
      overlayClassName='react-modal-overlay adjustment-of-position'
      onAfterClose={() => {
        setResetInitialState(true);
      }}
    >
      <AddUpdatesToReportContent
        resetInitialStateValues={{ resetInitialState, setResetInitialState }}
        selectedUpdates={selectedUpdates}
        fromMainList={fromMainList}
        addUpdatesToReportFunction={addUpdatesToReportFunction}
        setIsOpen={handleSetIsOpen}
      />
    </ModalComponent>
  );
};

const AddUpdatesToReportContent = ({
  selectedUpdates,
  fromMainList,
  addUpdatesToReportFunction,
  resetInitialStateValues,
  setIsOpen,
  backFunctionality,
}) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team, remainingCredits } = state ?? {};

  const { resetInitialState, setResetInitialState } = resetInitialStateValues ?? {};
  const [reportOption, setReportOption] = useState('NewReport');
  const [headlineInclude, setHeadlineInclude] = useState();
  const [selectedReport, setSelectedReport] = useState(null);
  const [numberOfCreditsForSummaries, setNumberOfCreditsForSummaries] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [reports, setReports] = useState(true);

  const { getReportList } = useGetReportList();
  const { isAuthenticated } = useAuth0();

  const initialCall = useRef(null);
  initialCall.current = async (source) => {
    try {
    } catch (error) {}
  };
  useEffect(() => {
    const source = new AbortController();
    initialCall.current(source);
    return () => {
      source.abort('Report list cancelled by the user');
    };
  }, []);

  useEffect(() => {
    if (resetInitialState) {
      setReportOption('NewReport');
      setHeadlineInclude('FullUpdate');
      setResetInitialState(false);
    }
  }, [resetInitialState]);

  const allTweetsCondition = selectedUpdates?.every((item) => item?.type === 'Tweet');
  const someTweetsCondition = selectedUpdates?.some((item) => item?.type === 'Tweet');
  const conditionForOneLineSummary = !allTweetsCondition;

  const allInteractions =
    selectedUpdates?.length > 0 && selectedUpdates.every((item) => item?.type === 'CrmInteraction');

  useEffect(() => {
    setHeadlineInclude(
      !allInteractions && selectedUpdates?.filter((item) => item?.type !== 'Tweet')?.length > 1
        ? 'OneLineSummary'
        : 'FullUpdate'
    );
  }, [selectedUpdates, allInteractions]);

  const { callCreditsForSummary } = useGetSummaryForContent();

  const callCreditsByType = async ({ type, updates, source }) => {
    try {
      const result = await callCreditsForSummary({
        contentType: type,
        objectIds: updates?.map((item) => item?.parentObjectID ?? item?.objectID ?? item?.objectId),
        source,
      });
      if (!!result) {
        return result;
      }
    } catch (error) {}
  };

  const callNumberOfCredits = useRef(null);
  const usersWithCredits = remainingCredits >= 0;
  callNumberOfCredits.current = async (source) => {
    const results = await getReportList({ source });
    if (!!results) {
      setReports(results);
      setSelectedReport(results?.[0]);
      if (usersWithCredits) {
        const groupBy = (arr) =>
          arr.reduce((acc, item) => {
            const objectKey = item?.['objectType'] ?? item?.['type'] ?? 'Unknown';
            (acc[objectKey] ??= []).push(item);
            return acc;
          }, {});

        const grouped = groupBy(selectedUpdates, 'type');
        let totalNumber = 0;
        for (const group in grouped) {
          const result = await callCreditsByType({ type: group, updates: grouped[group], source });
          if (!!result) {
            totalNumber += result;
          }
        }
        setNumberOfCreditsForSummaries(totalNumber);
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const source = new AbortController();
    callNumberOfCredits.current(source);
    return () => {
      source.abort('Number of credits for summaries cancelled by the user');
    };
  }, []);

  const conditionOfTailoredNote =
    usersWithCredits && selectedUpdates?.length > numberOfCreditsForSummaries && !isLoading;
  const conditionForSummaries =
    (!usersWithCredits || (remainingCredits > 0 && remainingCredits >= numberOfCreditsForSummaries)) &&
    !allTweetsCondition;
  const creditsCondition = usersWithCredits && isAuthenticated;
  const { newAgencyPlan } = useTopicOrClientWord();
  const { windowHeight } = useWindowHeight();

  const heightValue = (windowHeight * 90) / 100 - 80;

  const itemWord = allInteractions ? 'interaction' : 'update';

  return (
    <>
      <h3 className='p-4 border-bottom title-h4-bold mb-0'>
        Add {selectedUpdates?.length === 1 ? 'this' : `${selectedUpdates?.length}`} {itemWord}
        {selectedUpdates?.length === 1 ? '' : 's'} to a report {newAgencyPlan ? `for ${team?.teamName}` : ''}
      </h3>
      <CustomScrollbar
        autoHeight
        autoHeightMin='100%'
        autoHeightMax={`${heightValue}px`}
        className={'simple-scrollbar'}
      >
        <div className='p-4'>
          {fromMainList && (
            <>
              <div className='flex-centered mb-3 '>
                <p className='title-h5-m-bold flex-grow-1 mb-0'>
                  Choose a report to add {selectedUpdates?.length === 1 ? 'this' : 'these'} update
                  {selectedUpdates?.length === 1 ? '' : 's'} to
                </p>
                <button
                  className='general-button text-light-blue'
                  style={{ visibility: reportOption === 'ExistingReport' ? 'visible' : 'hidden' }}
                  onClick={() => {
                    setReportOption('NewReport');
                    setSelectedReport(reports[0]);
                  }}
                >
                  <span className='icon-long-arrow-left paragraph-p4 mr-2' /> Back
                </button>
              </div>

              {reportOption === 'ExistingReport' ? (
                <ListOfReports
                  reports={reports}
                  isLoading={isLoading}
                  selectedReportState={{ selectedReport, setSelectedReport }}
                />
              ) : (
                <div className={`flex-centered gap-10 mb-${allInteractions ? '4' : '5'}`}>
                  <button
                    className={`text-left general-button col border rounded specific-x-padding-20 specific-y-padding-10 hovered-button button-radio-box ${
                      reportOption === 'NewReport' ? 'selected' : ''
                    }`}
                    onClick={() => setReportOption('NewReport')}
                  >
                    <span className='select-item d-inline-block-centered mr-2' />A new report
                  </button>
                  <button
                    className={`text-left general-button col border rounded specific-x-padding-20 specific-y-padding-10 hovered-button button-radio-box ${
                      reportOption === 'ExistingReport' ? 'selected' : ''
                    }`}
                    onClick={() => setReportOption('ExistingReport')}
                  >
                    <span className='select-item d-inline-block-centered mr-2' />
                    An existing report
                  </button>
                </div>
              )}
            </>
          )}
          {!allInteractions && (
            <>
              <div className='mb-3'>
                <p className='title-h5-m-bold mb-0'>
                  Choose what to include under {selectedUpdates?.length === 1 ? 'the' : 'each'} headline
                </p>
                {someTweetsCondition && headlineInclude !== 'FullUpdate' && (
                  <p className='light-50-text paragraph-p2 mt-1 mb-0'>
                    Note that for X posts the full update is always included
                  </p>
                )}
              </div>
              <button
                className={`general-button d-block w-100 border rounded px-3 specific-y-padding-8 hovered-button button-radio-box text-left mb-4 ${
                  headlineInclude === 'FullUpdate' ? 'selected' : ''
                }`}
                style={{ minHeight: '46px' }}
                onClick={() => {
                  setHeadlineInclude('FullUpdate');
                }}
              >
                <span className='select-item d-inline-block-centered centered-2 mr-2' />
                The full update
              </button>

              <div className={`d-flex w-100 border rounded  mb-4`}>
                <div
                  className={`flex-grow-1 pointer hovered-button pl-3 button-radio-box specific-y-padding-11 ${
                    headlineInclude === 'OneLineSummary' ? 'selected' : ''
                  } ${conditionForOneLineSummary ? '' : 'light-30-text'}`}
                  onClick={() => {
                    if (conditionForOneLineSummary) {
                      setHeadlineInclude('OneLineSummary');
                    }
                  }}
                  style={{ alignContent: 'center' }}
                >
                  <span className='select-item d-inline-block-centered centered-2 mr-2' />
                  <span className='flex-grow-1 specific-y-padding-8 pr-3'>One-line summary of the update</span>
                </div>
              </div>

              <div className={`d-flex w-100 border rounded specific-bottom-margin-12`}>
                <div
                  className={`flex-grow-1 pointer hovered-button pl-3 button-radio-box ${
                    creditsCondition && !conditionOfTailoredNote && !isLoading ? '' : 'specific-y-padding-11'
                  }   ${headlineInclude === 'TailoredSummary' ? 'selected' : ''} ${
                    conditionForSummaries ? '' : 'light-30-text'
                  }`}
                  onClick={() => {
                    if (conditionForSummaries) {
                      setHeadlineInclude('TailoredSummary');
                    }
                  }}
                  style={{ alignContent: 'center' }}
                >
                  <span className='select-item d-inline-block-centered centered-2 mr-2' />
                  <span className='flex-grow-1 specific-y-padding-8 pr-3'>Tailored summary for {team?.teamName}</span>
                </div>
                {creditsCondition && (
                  <div className='border-left specific-left-padding-28'>
                    <CreditsButton
                      type={'AI'}
                      className={'font-weight-bold text-main-blue specific-y-padding-10 specific-top-margin-1  pr-3'}
                      numberOfCredits={numberOfCreditsForSummaries}
                    />
                  </div>
                )}
              </div>
              {conditionOfTailoredNote && headlineInclude === 'TailoredSummary' && (
                <p className='mb-0 light-50-text paragraph-p2'>
                  <span className='font-weight-bold'>Note</span>: You have already generated a tailored summary for{' '}
                  {selectedUpdates?.length === 1
                    ? 'this update'
                    : `${
                        numberOfCreditsForSummaries === 0
                          ? 'all'
                          : `${selectedUpdates?.length - numberOfCreditsForSummaries} of the updates selected`
                      }`}
                  . Credits are only deducted when a tailored summary is first generated.
                </p>
              )}
            </>
          )}

          <div className={`${allInteractions ? '' : 'pt-3 pb-4'} d-flex justify-content-between`}>
            <button
              className='general-button text-light-blue'
              style={{ visibility: backFunctionality ? 'visible' : 'hidden' }}
              onClick={backFunctionality}
            >
              <span className='icon-long-arrow-left paragraph-p4 mr-2' />
              Back{' '}
            </button>
            <button
              onClick={() => {
                addUpdatesToReportFunction({ selectedUpdates, headlineInclude, selectedReport, reportOption });
                setIsOpen(false);
              }}
              className='general-button rounded-pill action-button px-5 specific-y-padding-8'
            >
              {reportOption === 'NewReport' ? 'Create report' : 'Add to report'}
            </button>
          </div>
        </div>
      </CustomScrollbar>
    </>
  );
};

const ListOfReports = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team } = state ?? {};

  const { selectedReportState, isLoading, reports } = props;
  const { selectedReport, setSelectedReport } = selectedReportState ?? {};

  const { newAgencyPlan } = useTopicOrClientWord();

  return (
    <div className='mb-5'>
      <DropdownComponent
        mainItem={() => (
          <span className='specific-y-padding-5 d-inline-block' style={{ maxHeight: '47px' }}>
            {isLoading ? (
              <SkeletonTheme baseColor='#fff'>
                <Skeleton height={15} width={`200px`} />
              </SkeletonTheme>
            ) : (
              <span>{selectedReport ? selectedReport?.title : 'Select a report'}</span>
            )}
          </span>
        )}
      >
        <CustomScrollbar
          className={'simple-scrollbar'}
          autoHeight
          autoHeightMin='100%'
          autoHeightMax={'226px'}
          maximalThumbYSize={100}
        >
          {isLoading ? (
            <SkeletonTheme baseColor='#fff'>
              {Array(4)
                .fill()
                .map((item, index) => (
                  <div className='px-3 py-2 w-100' key={index}>
                    <Skeleton height={22} width={'100%'} />
                  </div>
                ))}
            </SkeletonTheme>
          ) : (
            <>
              {reports?.length === 0 ? (
                <div className='p-3 light-60-text font-italic'>
                  You have not created any reports yet {newAgencyPlan ? ` for ${team?.teamName}` : ''}
                </div>
              ) : (
                <ul className='policy-list px-0 py-1 mb-0'>
                  {reports?.map((item) => {
                    const { id, title } = item;
                    return (
                      <li
                        onClick={() => {
                          setSelectedReport(item);
                        }}
                        className='flex-centered specific-y-padding-8 px-3 list-action-button pointer'
                        key={`report-${id}`}
                      >
                        <span className='flex-grow-1'>{title}</span>
                        {selectedReport?.id === id && <span className='icon-tick paragraph-p4' />}
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </CustomScrollbar>
      </DropdownComponent>
    </div>
  );
};

export { AddUpdatesToReportContent };
export default AddUpdatesToReport;
